import { useState, useLayoutEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import Dropdown from '../common/Dropdown'
import PageModal from '../common/PageModal'
import SimpleButton from '../common/SimpleButton'
import { toast } from '../common/Toast'
import { createCorporateBranch, putCorporateBranch } from '../../actions/corporate'
import countries from '../../utils/countries.json'
import { convertToSimpleDateWithTime } from '../../utils/filters/date'
import { getImageByModel } from '../../utils/functions'
import { drAuthRules } from '../../utils/drConfig'
import { ExpandLessIcon, ExpandMoreIcon } from '../../icons'
import { no_image_200x200 } from '../../images'
import './view-edit-branch.scss'

const ViewEditBranches = ({ selectedBranch, isEditMode, closeModal, showSpinner, actions, companyId }) => {
  const { t } = useTranslation()
  const [branchName, setBranchName] = useState('')
  const [branchStatus, setBranchStatus] = useState('verified')
  const [country, setCountry] = useState('')
  const [address, setAddress] = useState('')
  const [city, setCity] = useState('')
  const [stateProvince, setStateProvince] = useState('')
  const [zipCode, setZipCode] = useState('')
  const [email, setEmail] = useState('')
  const [telephone, setTelephone] = useState('')
  const [region, setRegion] = useState('')
  const [members, setMembers] = useState(0)
  const [devices, setDevices] = useState([])
  const [subscriptions, setSubscriptions] = useState([])
  const [haveError, setHaveError] = useState({})
  const [isEditEnabled, setIsEditEnabled] = useState(isEditMode)
  const [areDevicesExpanded, setAreDevicesExpanded] = useState(false)
  const [areSubscriptionsExpanded, setAreSubscriptionsExpanded] = useState(false)
  const getCountryName = (code) => countries.find((c) => c.code === code)?.name || code

  useLayoutEffect(() => {
    if (selectedBranch) {
      setBranchName(selectedBranch.branchName || '')
      setBranchStatus(selectedBranch.status || 'verified')
      setCountry(selectedBranch.country || '')
      setAddress(selectedBranch.officeAddressOne || '')
      setCity(selectedBranch.city || '')
      setStateProvince(selectedBranch.stateProvinceRegion || '')
      setZipCode(selectedBranch.zipPostalCode || '')
      setEmail(selectedBranch.email || '')
      setTelephone(selectedBranch.telephone || '')
      setRegion(selectedBranch.region || '')
      setMembers(selectedBranch.members || 0)
      setDevices(selectedBranch.devices || [])
      setSubscriptions(selectedBranch.subscriptions || [])
    }
  }, [selectedBranch])

  const hasSameInput = () =>
    selectedBranch &&
    branchName === selectedBranch.branchName &&
    branchStatus === selectedBranch.status &&
    country === selectedBranch.country &&
    address === selectedBranch.officeAddressOne &&
    city === selectedBranch.city &&
    stateProvince === selectedBranch.stateProvinceRegion &&
    zipCode === selectedBranch.zipPostalCode &&
    email === selectedBranch.email &&
    telephone === selectedBranch.telephone

  const footerContent = () => {
    if (isEditEnabled) {
      return (
        <>
          <SimpleButton className="cancel-button" onClick={() => setIsEditEnabled(false)}>
            {t('cancel')}
          </SimpleButton>
          <SimpleButton className="submit-button" onClick={handleSubmit} disabled={showSpinner || hasSameInput() || !drAuthRules.isCompanyAccountUpdateAllowed}>
            {t('save')}
          </SimpleButton>
        </>
      )
    }
    return (
      <>
        <SimpleButton className="cancel-button" onClick={closeModal}>
          {t('close')}
        </SimpleButton>
        <SimpleButton className="action-button" onClick={() => setIsEditEnabled(true)}>
          {t('edit')}
        </SimpleButton>
      </>
    )
  }

  const handleSubmit = () => {
    if (!isValidInput()) {
      toast.error(t('error.fill_all_fields'))
      return
    }

    sendUpdateRequest()
  }

  const sendUpdateRequest = () => {
    const body = {
      branchName: branchName.trim(),
      email,
      country: country.trim(),
      officeAddressOne: address.trim(),
      city: city.trim(),
      stateProvinceRegion: stateProvince.trim(),
      zipPostalCode: zipCode.trim(),
      telephone: telephone.trim(),
    }

    if (selectedBranch) {
      actions.putCorporateBranch(companyId, selectedBranch.id, body)
    }

    closeModal()
  }

  const isValidInput = () => {
    let errors = {}
    let isValid = true

    if (!branchName.trim()) {
      errors = { ...errors, branchName: true }
      isValid = false
    }
    if (!country.trim()) {
      errors = { ...errors, country: true }
      isValid = false
    }
    if (!address.trim()) {
      errors = { ...errors, address: true }
      isValid = false
    }
    if (!city.trim()) {
      errors = { ...errors, city: true }
      isValid = false
    }
    if (!stateProvince.trim()) {
      errors = { ...errors, stateProvince: true }
      isValid = false
    }
    if (!zipCode.trim()) {
      errors = { ...errors, zipCode: true }
      isValid = false
    }

    setHaveError(errors)
    return isValid
  }

  return (
    <>
      <PageModal
        toggle
        onToggle={closeModal}
        className="view-edit-branch-modal modal-large"
        title={isEditEnabled ? t('organization_context.edit_branch') : t('details')}
        footerContent={footerContent()}
      >
        <div className="branch-modal-content">
          <div className="section-title">{t('general_information').toUpperCase()}</div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{t('branch')}</p>
              {isEditEnabled ? (
                <input
                  className={'input-item' + (haveError['branchName'] ? ' has-error' : '')}
                  value={branchName}
                  onChange={(e) => setBranchName(e.target.value)}
                />
              ) : (
                <div className="value">{branchName || '-'}</div>
              )}
            </div>
            <div className="input-wrapper"></div>
          </div>

          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{t('email')}</p>
              <div className="value">{email || '-'}</div>
            </div>
            <div className="input-wrapper">
              <p className="input-option">{t('members_single_multi')}</p>
              <div className="value">{members}</div>
            </div>
          </div>
          <div className="row-wrapper">
            <div className="row-wrapper">
              <div className="input-wrapper">
                <p className="input-option">{t('region')}</p>
                <div className="value">{region || '-'}</div>
              </div>
            </div>
            <div className="input-wrapper">
              <p className="input-option">{t('country')}</p>
              {isEditEnabled ? (
                <Dropdown
                  options={countries.map((c) => ({ key: c.code, value: c.name }))}
                  initialSelectedKey={country}
                  onChange={(c) => setCountry(c.key)}
                  hasError={haveError['country']}
                />
              ) : (
                <div className="value">{getCountryName(country)}</div>
              )}
            </div>
          </div>

          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{t('address')}</p>
              {isEditEnabled ? (
                <input className={'input-item' + (haveError['address'] ? ' has-error' : '')} value={address} onChange={(e) => setAddress(e.target.value)} />
              ) : (
                <div className="value">{address}</div>
              )}
            </div>
            <div className="input-wrapper">
              <p className="input-option">{t('city')}</p>
              {isEditEnabled ? <input className="input-item" value={city} onChange={(e) => setCity(e.target.value)} /> : <div className="value">{city}</div>}
            </div>
          </div>

          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{t('state_province')}</p>
              {isEditEnabled ? (
                <input
                  className={'input-item' + (haveError['stateProvince'] ? ' has-error' : '')}
                  value={stateProvince}
                  onChange={(e) => setStateProvince(e.target.value)}
                />
              ) : (
                <div className="value">{stateProvince}</div>
              )}
            </div>
            <div className="input-wrapper">
              <p className="input-option">{t('zip_postal_code')}</p>
              {isEditEnabled ? (
                <input className={'input-item' + (haveError['zipCode'] ? ' has-error' : '')} value={zipCode} onChange={(e) => setZipCode(e.target.value)} />
              ) : (
                <div className="value">{zipCode}</div>
              )}
            </div>
          </div>

          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{t('phone_number')}</p>
              {isEditEnabled ? (
                <input
                  className={'input-item' + (haveError['telephone'] ? ' has-error' : '')}
                  value={telephone}
                  onChange={(e) => setTelephone(e.target.value)}
                />
              ) : (
                <div className="value">{telephone}</div>
              )}
            </div>
            <div className="input-wrapper"></div>
          </div>

          {!isEditEnabled && (
            <>
              <div className="units-subscriptions-wrapper with-big-margin-top">
                <div className="separator" />
                <div className="units-header" onClick={() => setAreDevicesExpanded((prev) => !prev)}>
                  <div className="header-title">
                    <div className="title">{t('devices')}</div>
                    <div className="subtitle">
                      <span>{`${t('users-management.total-products')}: `}</span>
                      <span className="highlight">{devices.length}</span>
                    </div>
                  </div>
                  <div className="expand-button">{areDevicesExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</div>
                </div>
                {areDevicesExpanded &&
                  devices.map((device, index) => (
                    <div
                      className={'item-wrapper' + (index === 0 ? ' first-item-wrapper' : '') + (index === devices.length - 1 ? ' last-item-wrapper' : '')}
                      key={index}
                    >
                      <div className="item-image-wrapper">
                        <img src={getImageByModel(device.model?.replace(/-/g, ' '))} alt={device.model} className="item-image" />
                      </div>
                      <div className="item-title-wrapper">
                        <p className="title">{device.model}</p>
                        <p className="subtitle">{device.sn}</p>
                      </div>
                    </div>
                  ))}
                <div className="separator" />
                <div className="subscriptions-header" onClick={() => setAreSubscriptionsExpanded((prev) => !prev)}>
                  <div className="header-title">
                    <div className="title">{t('nav.subscriptions')}</div>
                    <div className="subtitle">
                      <span>{`${t('users-management.total_subscriptions')}: `}</span>
                      <span className="highlight">{subscriptions.length}</span>
                    </div>
                  </div>
                  <div className="expand-button">{areSubscriptionsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</div>
                </div>
                {areSubscriptionsExpanded &&
                  subscriptions.map((sub, index) => (
                    <div className="item-wrapper" key={index}>
                      <div className="item-image-wrapper">
                        <img src={sub.product?.images?.[0]?.url || no_image_200x200} alt={sub.title} className="item-image" />
                      </div>
                      <div className="item-title-wrapper">
                        <p className="title">{sub.title}</p>
                        <p className="subtitle">{t(`coltene_store.subscription_history.statuses.${sub.status}`, sub.status)}</p>
                        <p className="subtitle">{`${t('end_date')}: ${convertToSimpleDateWithTime(sub.endDate)}`}</p>
                      </div>
                    </div>
                  ))}
              </div>
            </>
          )}
        </div>
      </PageModal>
    </>
  )
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        createCorporateBranch,
        putCorporateBranch,
      },
      dispatch,
    ),
  }
}

export default connect(null, dispatchToProps)(ViewEditBranches)
